import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { usePageContentId } from '@confluence/page-context';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	useContentStatusContentStatusQuery$data,
	useContentStatusContentStatusQuery$variables,
} from './__generated__/useContentStatusContentStatusQuery.graphql.ts';

type UseContentStatusProps = {
	skip?: boolean;
	needRefetch?: boolean;
};

export const useContentStatus = (
	props: UseContentStatusProps = {},
): Omit<ReturnType<typeof useContentStatusWithRefetch>, 'refetch'> => {
	return useContentStatusWithRefetch(props, false);
};

export const useContentStatusWithRefetch = (
	{ skip = false }: UseContentStatusProps = {},
	returnRefreshFunction = true,
): {
	contentStatus: string | null | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	refetch: any;
	type: string | null | undefined;
} => {
	const [contentId] = usePageContentId();
	const query = graphql`
		query useContentStatusContentStatusQuery($contentId: ID!) {
			content(id: $contentId, status: ["current", "draft", "archived"]) {
				nodes {
					id
					status
					type
				}
			}
		}
	`;
	const options = {
		skip: skip || !contentId,
		variables: {
			contentId: contentId ?? '',
		},
	};

	let result;
	if (returnRefreshFunction) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		result = useQueryInterop<
			useContentStatusContentStatusQuery$data,
			useContentStatusContentStatusQuery$variables
		>(query, options);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		result = useQueryNoFurtherUpdateInterop<
			useContentStatusContentStatusQuery$data,
			useContentStatusContentStatusQuery$variables
		>(query, options);
	}

	const { data, loading, error } = result;
	const contentNode = data?.content?.nodes?.[0];
	return {
		contentStatus: contentNode?.status,
		loading,
		error,
		refetch: (result as any).refetch,
		type: contentNode?.type,
	};
};
